import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, SearchCheck } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";

const inr = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "");

export default function PublicStatus() {
  const [step, setStep] = useState("ask");
  const [v, setV] = useState({ mobile: "", email: "", code: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const run = async (fn) => { setErr(""); setBusy(true); try { await fn(); } catch (ex) { setErr(apiError(ex.response?.data?.detail)); } finally { setBusy(false); } };
  const request = (e) => { e.preventDefault(); run(async () => { const { data } = await api.post("/public/status/request", v); setMsg(data.message); setStep("code"); }); };
  const verify = (e) => { e.preventDefault(); run(async () => { const { data } = await api.post("/public/status/verify", v); setRes(data); setStep("done"); }); };
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200"><div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-3">
        <img src="/omi-logo.webp" alt="OMI Fintech Pvt Ltd" className="h-12 w-auto" /></div></header>
      <main className="max-w-xl mx-auto px-4 py-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 flex items-center gap-3" data-testid="status-heading"><SearchCheck className="h-8 w-8 text-blue-600" />Check your request status</h1>
        <p className="text-slate-600 mt-2 text-sm">Enter the mobile number and email you used. We'll email you a one-time code.</p>
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          {step === "done" ? (
            <div data-testid="status-results">
              <h2 className="font-bold text-lg">Hi {res.name || "there"},</h2>
              {res.items.length === 0 ? <p className="text-sm text-slate-600 mt-2">No requests found.</p> : res.items.map((i) => (
                <div key={i.ref} className="mt-4 rounded-xl border border-slate-200 p-4" data-testid="status-item">
                  <div className="flex justify-between gap-2"><span className="font-mono text-xs text-slate-500">{i.ref}</span><span className="text-xs text-slate-500">{i.submitted_on}</span></div>
                  <div className="font-semibold mt-1">{i.type}{i.product ? ` · ${i.product}` : ""} {inr(i.amount)}</div>
                  <div className="mt-2 inline-flex rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-bold" data-testid="status-label">{i.status}</div>
                  <p className="text-sm text-slate-600 mt-2">{i.next_step}</p>
                </div>
              ))}
              <p className="text-xs text-slate-500 mt-4">Loan approval, amount and terms are decided only by the lender.</p>
            </div>
          ) : (
            <form onSubmit={step === "ask" ? request : verify} className="space-y-4">
              <div><Label className="text-xs font-semibold text-slate-600">Mobile number</Label><Input type="tel" inputMode="numeric" className="mt-1 disabled:opacity-90 disabled:text-slate-800" value={v.mobile} onChange={set("mobile")} disabled={step === "code"} data-testid="status-mobile" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Email</Label><Input type="email" className="mt-1" value={v.email} onChange={set("email")} disabled={step === "code"} data-testid="status-email" /></div>
              {step === "code" && <>
                <p className="text-sm text-emerald-700" data-testid="status-code-sent">{msg}</p>
                <div><Label className="text-xs font-semibold text-slate-600">6-digit code</Label><Input inputMode="numeric" maxLength={6} className="mt-1 font-mono tracking-widest" value={v.code} onChange={set("code")} data-testid="status-code" /></div>
              </>}
              {err && <p className="text-sm text-rose-600" data-testid="status-error">{err}</p>}
              <Button type="submit" className="w-full h-11" disabled={busy} data-testid="status-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : step === "ask" ? "Email me a code" : "Show my status"}</Button>
              {step === "code" && <button type="button" className="text-xs text-slate-500 w-full" onClick={() => { setStep("ask"); setV({ ...v, code: "" }); }} data-testid="status-restart">Use different details</button>}
            </form>
          )}
        </div>
        <p className="text-center text-sm mt-6"><Link to="/apply-loan" className="text-blue-600 font-semibold" data-testid="status-apply-link">Apply for a loan</Link></p>
      </main>
      <SiteFooter />
    </div>
  );
}
